import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SessionUser } from "@gatiq/shared";
import { loginIdFields } from "@gatiq/shared";
import { api, ApiClientError, markSessionExpired, setSessionEndedHandler } from "@/lib/api";

/**
 * Holds the signed-in user for the whole app.
 *
 * No token is ever handled here: authentication rides in httpOnly cookies, so
 * JavaScript cannot read or leak it. `refresh()` simply re-asks the server who
 * we are.
 */
interface AuthValue {
  user: SessionUser | null;
  twoFaPending: boolean;
  twoFaEmail: string | null;
  twoFaDevCode: string | null;
  loading: boolean;
  /** Sign in as staff, scoped to an organization (or the platform). */
  /** `loginId` is whatever the person typed: an email or a mobile number (D-18). */
  login: (orgSlug: string | null, loginId: string, password: string) => Promise<SessionUser>;
  /** Sign in a karigar with a verified OTP. */
  loginWithOtp: (orgSlug: string, phone: string, code: string) => Promise<SessionUser>;
  /** Verify the 2FA code sent to the super admin's email. */
  verifyTwoFa: (code: string) => Promise<SessionUser>;
  /** Resend the 2FA code. */
  resendTwoFa: () => Promise<{ ok: boolean; devCode?: string }>;
  /** Clear the 2FA pending state without navigating. */
  clearTwoFa: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<SessionUser | null>;
  setUser: (user: SessionUser | null) => void;
}

const AuthContext = createContext<AuthValue>({
  user: null,
  twoFaPending: false,
  twoFaEmail: null,
  twoFaDevCode: null,
  loading: true,
  login: async () => {
    throw new Error("AuthProvider missing");
  },
  loginWithOtp: async () => {
    throw new Error("AuthProvider missing");
  },
  verifyTwoFa: async () => {
    throw new Error("AuthProvider missing");
  },
  resendTwoFa: async () => ({ ok: false }),
  clearTwoFa: () => undefined,
  logout: async () => undefined,
  refresh: async () => null,
  setUser: () => undefined,
});

export function useAuth(): AuthValue {
  return use(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [twoFaPending, setTwoFaPending] = useState(false);
  const twoFaOrgSlugRef = useRef<string | null>(null);
  const [twoFaEmail, setTwoFaEmail] = useState<string | null>(null);
  const [twoFaDevCode, setTwoFaDevCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /* Read by the session-ended handler, which must not re-subscribe on
   * every sign-in just to know whether anyone was signed in. */
  const userRef = useRef<SessionUser | null>(null);
  userRef.current = user;

  const refresh = useCallback(async (): Promise<SessionUser | null> => {
    try {
      const me = await api.get<SessionUser>("/auth/me");
      setUser(me);
      return me;
    } catch (err) {
      // A 401 here just means "not signed in" — it is not an error worth showing.
      if (!(err instanceof ApiClientError) || err.status !== 401) {
        console.error("Failed to load session", err);
      }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * The API client exchanges the refresh cookie on a 401 by itself; this fires
   * only when that fails too, meaning the session is genuinely over. Clearing
   * the user is all that is needed — `RequireAuth` is already watching it and
   * sends the person to the right login screen.
   */
  useEffect(() => {
    setSessionEndedHandler(() => {
      // A 401 with nobody signed in is an anonymous visitor, not an expiry —
      // someone opening a printed link for the first time, say.
      if (userRef.current) markSessionExpired();
      setUser(null);
    });
    return () => setSessionEndedHandler(null);
  }, []);

  const login = useCallback<AuthValue["login"]>(async (orgSlug, loginId, password) => {
    const path = orgSlug ? `/auth/${orgSlug}/login` : "/auth/login";
    const me = await api.post<SessionUser & { twoFaRequired?: boolean; devCode?: string }>(path, { ...loginIdFields(loginId), password });
    if (me.twoFaRequired) {
      twoFaOrgSlugRef.current = orgSlug || null;
      setTwoFaPending(true);
      setTwoFaEmail(me.email ?? null);
      if (me.devCode) {
        setTwoFaDevCode(me.devCode);
      }
    } else {
      setUser(me as SessionUser);
    }
    return me as SessionUser;
  }, []);

  const loginWithOtp = useCallback<AuthValue["loginWithOtp"]>(async (orgSlug, phone, code) => {
    const me = await api.post<SessionUser>(`/auth/${orgSlug}/otp/verify`, { phone, code });
    setUser(me);
    return me;
  }, []);

  const verifyTwoFa = useCallback(async (code: string) => {
    const base = twoFaOrgSlugRef.current ? `/auth/${twoFaOrgSlugRef.current}` : "/auth";
    const me = await api.post<SessionUser>(`${base}/2fa/verify`, { code });
    setTwoFaPending(false);
    setTwoFaDevCode(null);
    setUser(me);
    return me;
  }, []);

  const resendTwoFa = useCallback(async () => {
    const base = twoFaOrgSlugRef.current ? `/auth/${twoFaOrgSlugRef.current}` : "/auth";
    const result = await api.post<{ ok: boolean; devCode?: string }>(`${base}/2fa/request`);
    if (result.devCode) {
      setTwoFaDevCode(result.devCode);
    }
    return result;
  }, []);

  const clearTwoFa = useCallback(() => {
    setTwoFaPending(false);
    setTwoFaEmail(null);
    setTwoFaDevCode(null);
  }, []);

  /*
   * Sign out. The server revokes the refresh token and clears both cookies, but
   * the request is allowed to fail — someone on a flaky counter connection has
   * to be able to leave. Local state is cleared either way, so the app shows
   * nobody signed in immediately.
   *
   * The reload afterwards is the point: a service worker that still holds a
   * cached `/api/auth/me` would answer the next visit with the person who just
   * signed out, signing them straight back in. Reloading takes the page out
   * from under the old worker, which then clears itself.
   */
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Nothing to do — the local session is dropped regardless.
    } finally {
      setUser(null);
      setTwoFaPending(false);
      setTwoFaEmail(null);
      setTwoFaDevCode(null);
    }
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        // A new worker waiting means a deploy landed; skip the wait so the
        // stale cache is dropped now rather than on the next visit.
        if (reg?.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
        if (reg?.active) reg.active.postMessage({ type: "LOGOUT" });
      } catch {
        // Not being able to talk to the worker is not a reason to stay signed in.
      }
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, twoFaPending, twoFaEmail, twoFaDevCode, loading, login, loginWithOtp, verifyTwoFa, resendTwoFa, clearTwoFa, logout, refresh, setUser }),
    [user, twoFaPending, twoFaEmail, twoFaDevCode, loading, login, loginWithOtp, verifyTwoFa, resendTwoFa, clearTwoFa, logout, refresh, setUser],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
